import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode
} from 'react';
import { onAuthStateChanged, signInWithPopup } from 'firebase/auth';
import { firebaseAuth, firebaseEnabled, googleProvider } from '../config/firebase';
import {
  clearDevUser,
  endSession,
  getDevUser,
  rememberAccount,
  setDevUser,
  type SessionUser
} from './session';

interface AuthContextValue {
  user: SessionUser | null;
  initializing: boolean;
  firebaseEnabled: boolean;
  loginDev: (user: SessionUser) => void;
  loginGoogle: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(() => getDevUser());
  const [initializing, setInitializing] = useState<boolean>(() => !getDevUser() && firebaseEnabled);

  useEffect(() => {
    if (getDevUser() || !firebaseAuth) return;
    const unsubscribe = onAuthStateChanged(firebaseAuth, (firebaseUser) => {
      if (getDevUser()) return;
      if (firebaseUser) {
        setUser({
          uid: firebaseUser.uid,
          email: firebaseUser.email ?? '',
          name: firebaseUser.displayName ?? firebaseUser.email ?? '',
          avatarUrl: firebaseUser.photoURL
        });
      } else {
        setUser(null);
      }
      setInitializing(false);
    });
    return unsubscribe;
  }, []);

  // Keep the ID token fresh without waiting for a request to fail: Firebase refreshes
  // it lazily, but a backgrounded/asleep tab can let it lapse. Nudge a refresh every
  // 30 min and whenever the tab regains focus (getIdToken() only hits the network when
  // the token is within ~5 min of expiry, so this is cheap).
  useEffect(() => {
    const authInstance = firebaseAuth;
    if (!authInstance) return;
    const refresh = () => {
      const current = authInstance.currentUser;
      if (current) void current.getIdToken().catch(() => undefined);
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    const interval = window.setInterval(refresh, 30 * 60 * 1000);
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  const loginDev = useCallback((devUser: SessionUser) => {
    setDevUser(devUser);
    setUser(devUser);
    rememberAccount({ ...devUser, kind: 'dev' });
    setInitializing(false);
  }, []);

  const loginGoogle = useCallback(async () => {
    if (!firebaseAuth) return;
    clearDevUser();
    const credential = await signInWithPopup(firebaseAuth, googleProvider);
    const sessionUser = {
      uid: credential.user.uid,
      email: credential.user.email ?? '',
      name: credential.user.displayName ?? credential.user.email ?? '',
      avatarUrl: credential.user.photoURL
    };
    setUser(sessionUser);
    rememberAccount({ ...sessionUser, kind: 'google' });
  }, []);

  const logout = useCallback(async () => {
    await endSession();
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, initializing, firebaseEnabled, loginDev, loginGoogle, logout }),
    [user, initializing, loginDev, loginGoogle, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
